import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function getDeviceClientId() {
  let id = localStorage.getItem("cm_client_id");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2);
    localStorage.setItem("cm_client_id", id);
  }
  return id;
}

function extractSessionId() {
  const hash = window.location.hash || "";
  const m = hash.match(/session_id=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const sessionId = extractSessionId();
    if (sessionId) {
      (async () => {
        try {
          const { data } = await axios.post(
            `${API}/auth/session`,
            { device_client_id: getDeviceClientId() },
            { headers: { "X-Session-ID": sessionId }, withCredentials: true }
          );
          setUser(data);
          // strip the session_id fragment, then restore the page the user started from
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          try {
            const ret = sessionStorage.getItem("cm_auth_return_hash");
            sessionStorage.removeItem("cm_auth_return_hash");
            if (ret) window.location.hash = ret.replace(/^#/, "");
          } catch (_) {}
        } catch (e) {
          console.error("session exchange failed", e);
          window.history.replaceState(null, "", window.location.pathname);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    // Remember the exact page (and section anchor) the user started from, so we can return them there.
    try { sessionStorage.setItem("cm_auth_return_hash", window.location.hash || ""); } catch (_) {}
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname + window.location.search;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const loginWithPassword = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("logout failed", e);
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithPassword, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
